import React, { useState, useEffect } from 'react';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if running on iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if app is already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                     window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('PWA: beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay if not dismissed before
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        const lastShown = localStorage.getItem('pwa-prompt-last-shown');
        const now = Date.now();
        const dayInMs = 24 * 60 * 60 * 1000;
        
        if (!dismissed && (!lastShown || now - parseInt(lastShown) > dayInMs)) {
          setShowPrompt(true);
        }
      }, 3000); // Show after 3 seconds
    };

    // Listen for app installation
    const handleAppInstalled = () => {
      console.log('PWA: App was installed');
      setShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.setItem('pwa-installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if we should show iOS install prompt
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('ios-install-prompt-dismissed');
      const lastShown = localStorage.getItem('ios-install-prompt-last-shown');
      const now = Date.now();
      const dayInMs = 24 * 60 * 60 * 1000;
      
      if (!dismissed && (!lastShown || now - parseInt(lastShown) > dayInMs)) {
        setTimeout(() => setShowPrompt(true), 5000); // Show after 5 seconds on iOS
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    console.log('PWA: User clicked install');
    deferredPrompt.prompt();
    
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`PWA: User response to install prompt: ${outcome}`);
    
    if (outcome === 'accepted') {
      console.log('PWA: User accepted the install prompt');
    } else {
      console.log('PWA: User dismissed the install prompt');
      localStorage.setItem('pwa-prompt-dismissed', 'true');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    const storageKey = isIOS ? 'ios-install-prompt-dismissed' : 'pwa-prompt-dismissed';
    const lastShownKey = isIOS ? 'ios-install-prompt-last-shown' : 'pwa-prompt-last-shown';
    
    localStorage.setItem(storageKey, 'true');
    localStorage.setItem(lastShownKey, Date.now().toString());
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    const lastShownKey = isIOS ? 'ios-install-prompt-last-shown' : 'pwa-prompt-last-shown';
    localStorage.setItem(lastShownKey, Date.now().toString());
  };

  // Don't show if already installed or if prompt shouldn't be shown
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="pwa-install-prompt">
      <div className="pwa-install-content">
        <div className="pwa-install-icon">
          <img src="/icon-192x192.svg" alt="JobMockAI" width="48" height="48" />
        </div>
        
        <div className="pwa-install-text">
          <h3>Install JobMockAI</h3>
          <p>
            {isIOS 
              ? "Add JobMockAI to your home screen for quick access and a native app experience!"
              : "Install JobMockAI for faster access and offline practice sessions!"
            }
          </p>
        </div>

        <div className="pwa-install-actions">
          {isIOS ? (
            <>
              <button 
                className="pwa-install-btn pwa-install-btn-primary"
                onClick={handleDismiss}
              >
                Got it!
              </button>
              <div className="ios-install-instructions">
                <p>
                  Tap <span className="ios-share-icon">⎋</span> then "Add to Home Screen"
                </p>
              </div>
            </>
          ) : (
            <>
              <button 
                className="pwa-install-btn pwa-install-btn-primary"
                onClick={handleInstallClick}
                disabled={!deferredPrompt}
              >
                Install App
              </button>
              <button 
                className="pwa-install-btn pwa-install-btn-secondary"
                onClick={handleRemindLater}
              >
                Later
              </button>
            </>
          )}
          
          <button 
            className="pwa-install-close"
            onClick={handleDismiss}
            aria-label="Close install prompt"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;